/**
 * Renderer-side AI helpers for the Design tab's modular items — the
 * bi-directional slug ↔ details sync. Each function builds a focused prompt
 * (prompts.ts), runs a one-shot `design:aiChat`, and parses the structured
 * `## LABEL` payload back out. The proposals they return are reviewed as a
 * diff before anything is written (ItemProposalDialog), so this layer only
 * produces text — it never mutates the section itself.
 */
import type { CrucibleClient } from '@volley/crucible-client';
import { aiChat } from '../anvil-api';
import { parseSections } from './section-response';
import { parseItems, type DesignItem } from './section-items';
import {
  itemDetailsFromSlugPrompt,
  itemSlugFromDetailsPrompt,
  newItemPrompt,
  itemEntriesPrompt,
  reviseItemPrompt,
  type ItemAiContext,
} from '../prompts';

async function runChat(
  client: CrucibleClient,
  gameId: string,
  system: string,
  user: string,
): Promise<string> {
  const { text } = await aiChat(client, {
    gameId,
    system,
    messages: [{ role: 'user', content: user }],
  });
  return text;
}

/** First non-empty line, stripped of wrapping quotes / italic markers. */
function firstLine(text: string): string {
  const line = text
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!line) return '';
  return line.replace(/^["'_*]+|["'_*]+$/g, '').trim();
}

/** Propose a rewritten details body that fits the item's name + slug. */
export async function proposeItemDetails(
  client: CrucibleClient,
  gameId: string,
  ctx: ItemAiContext,
): Promise<string> {
  const { system, user } = itemDetailsFromSlugPrompt(ctx);
  const text = await runChat(client, gameId, system, user);
  const { body } = parseSections(text, ['BODY'] as const);
  return (body || text).trim();
}

/** Propose a one-line slug distilled from the item's details body. */
export async function proposeItemSlug(
  client: CrucibleClient,
  gameId: string,
  ctx: ItemAiContext,
): Promise<string> {
  const { system, user } = itemSlugFromDetailsPrompt(ctx);
  const text = await runChat(client, gameId, system, user);
  const { slug } = parseSections(text, ['SLUG'] as const);
  return firstLine(slug || text);
}

/**
 * Apply a freeform designer instruction to one WHOLE item — its slug, its
 * details, and (for a top-level item) its sub-items: the model can add, edit,
 * or remove `###` entries. Returns the revised slug + body + children (children
 * is `[]` for a leaf / when `allowChildren` is false). Returns `null` if the
 * model didn't return a parseable item, so the caller can surface that rather
 * than silently wipe the item.
 */
export async function reviseItem(
  client: CrucibleClient,
  gameId: string,
  ctx: ItemAiContext,
  instruction: string,
  opts: { currentItemMarkdown: string; allowChildren: boolean },
): Promise<{ slug: string; body: string; children: DesignItem[] } | null> {
  const { system, user } = reviseItemPrompt(ctx, instruction, opts);
  const text = await runChat(client, gameId, system, user);
  const parsed = opts.allowChildren
    ? parseSections(text, ['SLUG', 'BODY', 'ENTRIES'] as const)
    : { ...parseSections(text, ['SLUG', 'BODY'] as const), entries: '' };

  const slugText = parsed.slug.trim();
  const bodyText = parsed.body.trim();
  const entriesText = parsed.entries.trim();
  // Nothing usable came back — let the caller surface it instead of wiping.
  if (!slugText && !bodyText && !entriesText) return null;

  // Existing sub-items, recovered from the item we sent — kept when the model
  // omits ENTRIES (instruction didn't touch sub-items).
  const existingChildren =
    parseItems(opts.currentItemMarkdown).items[0]?.children.map((c) => ({ ...c, children: [] })) ??
    [];
  let children: DesignItem[];
  if (!opts.allowChildren) {
    children = [];
  } else if (entriesText) {
    const proposed = parseItems(entriesText).items.map((c) => ({ ...c, children: [] }));
    // ENTRIES present but unparseable (prose, wrong heading level) → keep the
    // existing sub-items rather than wiping them on a malformed reply.
    children = proposed.length > 0 ? proposed : existingChildren;
  } else {
    children = existingChildren;
  }

  return {
    // Fall back to the current values when the model leaves a field blank, so a
    // partial reply can't blank the slug or wipe the details.
    slug: firstLine(slugText || ctx.slug),
    body: bodyText || ctx.body,
    children,
  };
}

/** Draft both slug + details for a brand-new item from just its name. */
export async function proposeNewItem(
  client: CrucibleClient,
  gameId: string,
  ctx: ItemAiContext,
): Promise<{ slug: string; body: string }> {
  const { system, user } = newItemPrompt(ctx);
  const text = await runChat(client, gameId, system, user);
  const parsed = parseSections(text, ['SLUG', 'BODY'] as const);
  return { slug: firstLine(parsed.slug), body: (parsed.body || '').trim() };
}

/**
 * Propose the named child entries that belong under a heading, plus a rewritten
 * parent `body` (the shared framing only — the per-instance details move into
 * the entries, so the parent stops duplicating them). Entries are flattened to
 * one level.
 */
export async function proposeItemEntries(
  client: CrucibleClient,
  gameId: string,
  ctx: ItemAiContext,
): Promise<{ body: string; entries: DesignItem[] }> {
  const { system, user } = itemEntriesPrompt(ctx);
  const text = await runChat(client, gameId, system, user);
  const { framing, entries } = parseSections(text, ['FRAMING', 'ENTRIES'] as const);
  return {
    body: (framing || '').trim(),
    entries: parseItems(entries || '').items.map((it) => ({ ...it, children: [] })),
  };
}
